import {
  APIUsageHistory,
  DataTable,
  Typography,
  useForm,
  Wrapper,
} from "@ecologi/react-components";
import { Link } from "@ecologi/react-components/src";
import { Button } from "@ecologi/react-components/src/Button";
import { Card } from "@ecologi/react-components/src/Card";
import { FormSection } from "@ecologi/react-components/src/FormSection";
import { AccountPagesHeader } from "@ecologi/react-components/src/PageHeaders";
import { TakeToAccount } from "@ecologi/react-components/src/TakeToAccount";
import { TextField } from "@ecologi/react-components/src/TextField";
import {
  API_DEFAULT_REQUESTS_PER_SECOND,
  CarbonRemovalProjectType,
  customOffsetsCosts,
  customRemovalCosts,
  customRestorationCosts,
  customTreesCosts,
  formatNumber,
  kgToTonnes,
  RestorationProjectType,
  UKReforestationProject,
  USReforestationProject,
  AUReforestationProject,
  BRReforestationProject,
  User,
} from "@ecologi/shared";
import styled from "@emotion/styled";
import { useMemo, useState } from "react";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { object, string } from "yup";
import { Head } from "../../../components";
import {
  APIError,
  ApiKey,
  createAPIKey,
  formatAPIErrorForView,
  updateUser,
  withPrivatePageGuard,
} from "../../../lib";
import { useStore } from "../../../store";
import { EmailPreviewThumbnail } from "./email-preview-thumbnail";
import { getApiHistoryForYear } from "../../../lib/api/impact-order-reports";

const ApiPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.snowdrop};
  @media ${({ theme }) => theme.bp.desktopUp} {
    padding-bottom: 56px;
  }
`;

const TableKey = styled.div`
  display: flex;
  flex-direction: column;
`;

const TableDate = styled.div`
  margin-top: 24px;
  align-self: flex-end;
  font-size: 12px;
`;

const PageButton = styled(Button)`
  width: fit-content;
`;

const DescriptionField = styled(TextField)`
  margin-bottom: 44px;
`;

const TermsAndConditions = styled(Typography)`
  /* Color does not exist - need to check with design team */
  color: rgba(82, 88, 91, 1);
`;

const FormButtons = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const ApiCards = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 32px;
`;

const PoPageButton = styled(PageButton)`
  margin-top: 16px;
`;

const SaveButton = styled(Button)`
  margin-right: 10px;
`;

const PoNumberActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const PricingTable = styled(DataTable)`
  margin-top: 24px;
`;

const apiKeySchema = object().shape({
  description: string().default("").min(1, "Please enter a description"),
});

const poNumberSchema = object().shape({
  apiPoNumber: string()
    .default("")
    .max(30, "PO number must be 30 characters or less"),
});

const ApiPageComponent = () => {
  const { user, setUser, isCheckingUserSession } = useStore();
  const { userId, apiKeys } = user;

  const [isFormVisible, setFormVisibility] = useState(apiKeys.length === 0);
  const [isPoNumberFormVisible, setPoNumberFormVisibility] = useState(false);

  const [createAPIKeyMutation, { isLoading }] = useMutation(createAPIKey, {
    onSuccess: (user) => setUser(new User(user)),
    onError: (error: APIError) => {
      toast.error(formatAPIErrorForView(error));
    },
  });

  const [updateUserMutation, { isLoading: isUpdatingUser }] = useMutation(
    updateUser,
    {
      onSuccess: (response) => {
        setUser(new User(response.user));
        toast.success("PO number updated successfully");
      },
      onError: (error: APIError) => {
        toast.error(formatAPIErrorForView(error));
      },
    }
  );

  const form = useForm<{ description: string }>({
    validationSchema: apiKeySchema,
    initialValues: {
      description: "",
    },
    onSubmit: async ({ description }) => {
      await createAPIKeyMutation({ userId: userId, description });
      setFormVisibility(false);
      form.resetForm();
    },
  });

  const poNumberForm = useForm<{ apiPoNumber: string }>({
    validationSchema: poNumberSchema,
    initialValues: {
      apiPoNumber: user.apiPoNumber || "",
    },
    onSubmit: async ({ apiPoNumber }) => {
      await updateUserMutation({ userId, fieldsToUpdate: { apiPoNumber } });
      setPoNumberFormVisibility(false);
    },
  });

  if (isCheckingUserSession || !user) {
    return null;
  }

  const formattedApiKeys = useMemo(() => {
    return user.apiKeys.map((apiKey) => {
      const { date, description, key } = new ApiKey(apiKey);
      return [
        description,
        <TableKey>
          <div data-testid="api-key-value">{key}</div>
          <TableDate>created {date}</TableDate>
        </TableKey>,
      ];
    });
  }, [user.apiKeys]);

  const removalCosts = customRemovalCosts[CarbonRemovalProjectType.Biochar];
  const restorationCosts =
    customRestorationCosts[RestorationProjectType.Wildflowers];

  const apiPricingRows = [
    [
      "GBP",
      `£${customTreesCosts.GBP.toFixed(2)}`,
      `£${UKReforestationProject.prices.GBP.toFixed(2)}`,
      `£${USReforestationProject.prices.GBP.toFixed(2)}`,
      `£${AUReforestationProject.prices.GBP.toFixed(2)}`,
      `£${BRReforestationProject.prices.GBP.toFixed(2)}`,
      `£${customOffsetsCosts.GBP.toFixed(2)}`,
      `£${(removalCosts.GBP / 1000).toFixed(2)}`,
      `£${restorationCosts.GBP.toFixed(2)}`,
    ],
    [
      "USD",
      `$${customTreesCosts.USD.toFixed(2)}`,
      `$${UKReforestationProject.prices.USD.toFixed(2)}`,
      `$${USReforestationProject.prices.USD.toFixed(2)}`,
      `$${AUReforestationProject.prices.USD.toFixed(2)}`,
      `$${BRReforestationProject.prices.USD.toFixed(2)}`,
      `$${customOffsetsCosts.USD.toFixed(2)}`,
      `$${(removalCosts.USD / 1000).toFixed(2)}`,
      `$${restorationCosts.USD.toFixed(2)}`,
    ],
    [
      "EUR",
      `€${customTreesCosts.EUR.toFixed(2)}`,
      `€${UKReforestationProject.prices.EUR.toFixed(2)}`,
      `€${USReforestationProject.prices.EUR.toFixed(2)}`,
      `€${AUReforestationProject.prices.EUR.toFixed(2)}`,
      `€${BRReforestationProject.prices.EUR.toFixed(2)}`,
      `€${customOffsetsCosts.EUR.toFixed(2)}`,
      `€${(removalCosts.EUR / 1000).toFixed(2)}`,
      `€${restorationCosts.EUR.toFixed(2)}`,
    ],
    [
      "AUD",
      `A$${customTreesCosts.AUD.toFixed(2)}`,
      `A$${UKReforestationProject.prices.AUD.toFixed(2)}`,
      `A$${USReforestationProject.prices.AUD.toFixed(2)}`,
      `A$${AUReforestationProject.prices.AUD.toFixed(2)}`,
      `A$${BRReforestationProject.prices.AUD.toFixed(2)}`,
      `A$${customOffsetsCosts.AUD.toFixed(2)}`,
      `A$${(removalCosts.AUD / 1000).toFixed(2)}`,
      `A$${restorationCosts.AUD.toFixed(2)}`,
    ],
    [
      "CAD",
      `CA$${customTreesCosts.CAD.toFixed(2)}`,
      `CA$${UKReforestationProject.prices.CAD.toFixed(2)}`,
      `CA$${USReforestationProject.prices.CAD.toFixed(2)}`,
      `CA$${AUReforestationProject.prices.CAD.toFixed(2)}`,
      `CA$${BRReforestationProject.prices.CAD.toFixed(2)}`,
      `CA$${customOffsetsCosts.CAD.toFixed(2)}`,
      `CA$${(removalCosts.CAD / 1000).toFixed(2)}`,
      `CA$${restorationCosts.CAD.toFixed(2)}`,
    ],
  ];

  const {
    isSubmitting,
    handleSubmit,
    isValid,
    errors: { description },
  } = form;

  return (
    <>
      <Head title="Impact API" />
      <ApiPageContainer>
        <AccountPagesHeader
          title="Impact API"
          subtitle="Embed climate action into your customer experience"
        />
        <Wrapper>
          <APIUsageHistory
            user={user}
            getApiHistoryForYear={getApiHistoryForYear}
          />
          <FormSection title="Setup">
            <Typography use="body1" as="p">
              Check out our{" "}
              <Link href="https://docs.ecologi.com/">API documentation</Link>{" "}
              for the most comprehensive guide on how to integrate with our
              Impact API.
            </Typography>
            <Typography use="body1" as="p">
              Impact purchased via the API will be billed on the 1st of every
              month and will include a "pending" disclaimer until its successful
              payment.
            </Typography>
          </FormSection>
          <FormSection title="Access keys">
            <Typography use="body1" as="p">
              To integrate with our API, simply generate an access key below.
              You can create up to 5 keys, label them as you like, and each one
              can handle up to {API_DEFAULT_REQUESTS_PER_SECOND} requests per
              second.
            </Typography>
            {user.apiKeys.length > 0 && (
              <DataTable
                columnContentTypes={["text", "node"]}
                headings={["Description", "Key"]}
                rows={formattedApiKeys}
              />
            )}
            {!isFormVisible && user.apiKeys.length < 5 && (
              <PageButton
                darkGreen
                onClick={(e) => {
                  e.preventDefault();
                  setFormVisibility(true);
                }}
                text="Create new API key"
              />
            )}
            {isFormVisible && (
              <>
                <form onSubmit={handleSubmit}>
                  <DescriptionField
                    {...form.getFieldProps("description", "Description")}
                    isDisabled={isSubmitting}
                    maxLength={30}
                    errorText={description}
                    autoFocus
                    placeholder="Enter a memorable description"
                    showCharacterCount
                  />
                  <FormButtons>
                    <Button
                      loading={isSubmitting}
                      disabled={!isValid || isSubmitting}
                      extra={{ style: { marginRight: "10px" } }}
                      submit
                      darkGreen
                      text="Create key"
                    />
                    <Button
                      disabled={isLoading}
                      onClick={(e) => {
                        e.preventDefault();
                        form.resetForm();
                        setFormVisibility(false);
                      }}
                      textOnly
                      invert
                      text="Cancel"
                    />
                  </FormButtons>
                </form>
              </>
            )}
            {!isFormVisible && user.apiKeys.length === 5 && (
              <Typography use="label" as="p">
                Maximum 5 API keys. Please contact{" "}
                <Typography
                  use="inlineLink"
                  href="mailto:hq@ecologi.com?subject=Request for more API keys"
                  rel="noopener noreferrer"
                  target="_blank"
                  title="Contact a member of our team"
                >
                  hq@ecologi.com
                </Typography>{" "}
                if you need more.
              </Typography>
            )}
            <TermsAndConditions use="label" as="p">
              By using our API you're agreeing to our{" "}
              <TermsAndConditions
                use="inlineLink"
                href="/pages/terms-and-conditions"
                rel="noopener noreferrer"
                target="_blank"
                title="View the Ecologi Terms and Conditions"
              >
                Terms & Conditions
              </TermsAndConditions>
              .
            </TermsAndConditions>
          </FormSection>
          <FormSection title="Pricing">
            {user.bespokeApiPrices ? (
              <>
                <Typography use="body1" as="p">
                  Your bespoke API pricing:
                </Typography>
                <Typography use="body1" as="p">
                  <strong>Tree:</strong>{" "}
                  {formatNumber({
                    value: user.bespokeApiPrices.TREES,
                    currency: user.preferredCurrency,
                  })}
                </Typography>
                <Typography use="body1" as="p">
                  <strong>Carbon avoidance (tonne):</strong>{" "}
                  {formatNumber({
                    value: user.bespokeApiPrices.CARBON_OFFSETS,
                    currency: user.preferredCurrency,
                  })}
                </Typography>
                <Typography use="body1" as="p">
                  <strong>Carbon removal (kg):</strong>{" "}
                  {formatNumber({
                    value: kgToTonnes(user.bespokeApiPrices.CARBON_REMOVAL),
                    currency: user.preferredCurrency,
                  })}
                </Typography>
                <Typography use="body1" as="p">
                  <strong>Habitat restoration (m²):</strong>{" "}
                  {formatNumber({
                    value: user.bespokeApiPrices.HABITAT_RESTORATION,
                    currency: user.preferredCurrency,
                  })}
                </Typography>
              </>
            ) : (
              <>
                <Typography use="body1" as="p">
                  Here is our current live Impact API pricing:
                </Typography>
                <PricingTable
                  columnContentTypes={[
                    "text",
                    "text",
                    "text",
                    "text",
                    "text",
                    "text",
                    "text",
                    "text",
                    "text",
                  ]}
                  headings={[
                    "Currency",
                    "Tree",
                    "UK tree",
                    "US tree",
                    "AU tree",
                    "BR tree",
                    "Avoidance/t",
                    "Removal/kg",
                    "Habitat/m²",
                  ]}
                  rows={apiPricingRows}
                />
              </>
            )}
          </FormSection>
          <FormSection title="Invoice settings">
            <Typography use="body1" as="p">
              Configure a PO (purchase order) number to appear on your monthly
              API invoices.
            </Typography>
            {!isPoNumberFormVisible && (
              <>
                {user.apiPoNumber && (
                  <Typography use="body1" as="p">
                    <strong>Current PO number:</strong> {user.apiPoNumber}
                  </Typography>
                )}
                {user.apiPoNumber ? (
                  <PoNumberActions>
                    <PageButton
                      darkGreen
                      onClick={(e) => {
                        e.preventDefault();
                        poNumberForm.setFieldValue(
                          "apiPoNumber",
                          user.apiPoNumber || ""
                        );
                        setPoNumberFormVisibility(true);
                      }}
                      text="Update"
                    />
                    <PageButton
                      invert
                      onClick={async (e) => {
                        e.preventDefault();
                        await updateUserMutation({
                          userId,
                          fieldsToUpdate: { apiPoNumber: "" },
                        });
                      }}
                      disabled={isUpdatingUser}
                      text="Clear"
                    />
                  </PoNumberActions>
                ) : (
                  <PoPageButton
                    darkGreen
                    onClick={(e) => {
                      e.preventDefault();
                      poNumberForm.setFieldValue("apiPoNumber", "");
                      setPoNumberFormVisibility(true);
                    }}
                    text="Add"
                  />
                )}
              </>
            )}
            {isPoNumberFormVisible && (
              <form onSubmit={poNumberForm.handleSubmit}>
                <DescriptionField
                  {...poNumberForm.getFieldProps("apiPoNumber", "PO number")}
                  isDisabled={poNumberForm.isSubmitting}
                  maxLength={30}
                  errorText={poNumberForm.errors.apiPoNumber}
                  autoFocus
                  placeholder="Enter your PO number (optional)"
                  showCharacterCount
                />
                <FormButtons>
                  <SaveButton
                    loading={poNumberForm.isSubmitting}
                    disabled={
                      !poNumberForm.isValid || poNumberForm.isSubmitting
                    }
                    submit
                    darkGreen
                    text="Save"
                  />
                  <Button
                    disabled={poNumberForm.isSubmitting}
                    onClick={(e) => {
                      e.preventDefault();
                      poNumberForm.resetForm();
                      setPoNumberFormVisibility(false);
                    }}
                    textOnly
                    invert
                    text="Cancel"
                  />
                </FormButtons>
              </form>
            )}
          </FormSection>

          <FormSection title="Recipient emails">
            <Typography use="body1" as="p">
              Send a notification email to a third-party recipient when impact
              is purchased via the API — useful for gifting trees, carbon
              avoidance or habitat restoration to your customers.
            </Typography>
            {user.isApiRecipientEmailsEnabled ? (
              <Typography use="body1" as="p">
                Recipient emails are enabled on your account. See the{" "}
                <Link
                  href="https://docs.ecologi.com/#billing"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  API documentation
                </Link>{" "}
                for how to pass a <code>recipientEmail</code> on impact
                purchases.
              </Typography>
            ) : (
              <Typography use="body1" as="p">
                Recipient emails are not yet enabled on your account. Please
                contact{" "}
                <Typography
                  use="inlineLink"
                  href="mailto:support@ecologi.com?subject=Enable API recipient emails"
                  rel="noopener noreferrer"
                  target="_blank"
                  title="Contact a member of our team"
                >
                  support@ecologi.com
                </Typography>{" "}
                to enable this feature.
              </Typography>
            )}
            <EmailPreviewThumbnail />
          </FormSection>

          <FormSection title="Integrations">
            <Typography use="body1" as="p">
              Looking for a "no-tech" solution? Check out our third party
              integrations to start automating your climate action today.
            </Typography>
            <ApiCards>
              <Card
                imageUrl="/images/app/shopify.png"
                linkText="View Shopify plugin"
                text="You can plant a tree for every unit you sell or plant trees in relation to your revenue, all automatically. It takes just 2 minutes to set up."
                title="Shopify"
                variant="profile"
                extra={{
                  href: "https://apps.shopify.com/ecologi",
                  rel: "noopener noreferrer",
                  target: "_blank",
                  title: "View the Shopify plugin (new tab)",
                }}
              />
              <Card
                imageUrl="/images/app/zapier.png"
                linkText="See our Zapier integration"
                text="You can plant trees when any of Zapier’s thousands of triggers fire. From Stripe, to Typeform, you can automate your climate impact."
                title="Zapier"
                variant="profile"
                extra={{
                  href: "https://zapier.com/apps/ecologi/integrations",
                  rel: "noopener noreferrer",
                  target: "_blank",
                  title: "See the Zapier intergration (new tab)",
                }}
              />
            </ApiCards>
          </FormSection>
        </Wrapper>
        <TakeToAccount />
      </ApiPageContainer>
    </>
  );
};

export const ApiPage = withPrivatePageGuard(ApiPageComponent);

ApiPage.layoutProps = {
  isProfile: true,
};
